<template>
  <div class="wheelchairs-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">Wheelchairs</h1>
        <p class="page-subtitle">Professional wheelchair solutions for diverse needs</p>
      </div>
    </div>

    <!-- 轮椅分类导航 -->
    <div class="category-nav">
      <div class="container">
        <div class="category-tabs">
          <button 
            v-for="category in categories" 
            :key="category.id"
            :class="['category-tab', { active: selectedCategory === category.id }]"
            @click="selectCategory(category.id)"
          >
            <span class="category-icon">{{ category.icon }}</span>
            <span class="category-name">{{ category.name }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 轮椅筛选和展示 -->
    <div class="wheelchairs-section">
      <div class="container">
        <!-- 高级筛选器 -->
        <div class="advanced-filters">
          <div class="filter-header">
            <h3>Filters</h3>
            <button class="clear-filters" @click="clearFilters">Clear Filters</button>
          </div>
          
          <div class="filter-row">
            <!-- 基础筛选 -->
            <div class="filter-group">
              <label>Wheelchair Type</label>
              <select v-model="selectedCategory" class="filter-select">
                <option value="">All Types</option>
                <option value="power">Power Wheelchairs</option>
                <option value="manual">Manual Wheelchairs</option>
                <option value="sports">Sports Wheelchairs</option>
                <option value="care">Care Wheelchairs</option>
                <option value="children">Children's Wheelchairs</option>
              </select>
            </div>
            
            <div class="filter-group">
              <label>User Group</label>
              <select v-model="selectedUser" class="filter-select">
                <option value="">All</option>
                <option value="adult">Adult</option>
                <option value="child">Child</option>
                <option value="elderly">Elderly</option>
              </select>
            </div>
            
            <div class="filter-group">
              <label>Brand</label>
              <select v-model="selectedBrand" class="filter-select">
                <option value="">All Brands</option>
                <option value="permobil">Permobil</option>
                <option value="invacare">Invacare</option>
                <option value="sunrise">Sunrise</option>
                <option value="ottobock">Ottobock</option>
              </select>
            </div>
          </div>
          
          <div class="filter-row">
            <!-- 价格范围滑块 -->
            <div class="filter-group price-filter">
              <label>Price Range: ¥{{ priceRange[0].toLocaleString() }} - ¥{{ priceRange[1].toLocaleString() }}</label>
              <div class="price-slider">
                <input 
                  type="range" 
                  v-model="priceRange[0]" 
                  :min="0" 
                  :max="50000" 
                  step="1000"
                  class="range-slider"
                />
                <input 
                  type="range" 
                  v-model="priceRange[1]" 
                  :min="0" 
                  :max="50000" 
                  step="1000"
                  class="range-slider"
                />
              </div>
            </div>
            
            <!-- 评分筛选 -->
            <div class="filter-group">
              <label>Minimum Rating</label>
              <div class="rating-filter">
                <span 
                  v-for="rating in 5" 
                  :key="rating"
                  :class="['star', { active: selectedRating >= rating }]"
                  @click="selectedRating = rating"
                >
                  ★
                </span>
              </div>
            </div>
            
            <!-- 功能筛选 -->
            <div class="filter-group">
              <label>Features</label>
              <div class="feature-checkboxes">
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="foldable">
                  <span>Foldable</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="adjustable">
                  <span>Adjustable</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="lightweight">
                  <span>Lightweight</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="smart">
                  <span>Smart Control</span>
                </label>
              </div>
            </div>
          </div>
          
          <!-- 排序选项 -->
          <div class="filter-row">
            <div class="filter-group">
              <label>Sort By</label>
              <select v-model="sortBy" class="filter-select">
                <option value="default">Default</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Top Rated</option>
                <option value="newest">Newest</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
            
            <div class="filter-group">
              <label>Items per page</label>
              <select v-model="itemsPerPage" class="filter-select">
                <option value="6">6</option>
                <option value="12">12</option>
                <option value="24">24</option>
              </select>
            </div>
          </div>
        </div>

        <!-- 筛选结果统计 -->
        <div class="filter-stats">
          <span>Found {{ totalFilteredProducts }} products</span>
          <span v-if="activeFilters.length > 0" class="active-filters">
            Applied filters: {{ activeFilters.join(', ') }}
          </span>
        </div>

        <!-- 轮椅网格 -->
        <div class="wheelchairs-grid">
          <div 
            v-for="wheelchair in paginatedProducts" 
            :key="wheelchair.id" 
            class="wheelchair-card"
            @click="viewProduct(wheelchair)"
          >
            <div class="wheelchair-image">
              <img :src="wheelchair.image" :alt="wheelchair.name" />
              <div class="wheelchair-badge" v-if="wheelchair.badge">{{ wheelchair.badge }}</div>
              <div class="wheelchair-rating">
                <span v-for="star in 5" :key="star" :class="['star', { filled: star <= wheelchair.rating }]">
                  ★
                </span>
                <span class="rating-text">({{ wheelchair.rating }})</span>
              </div>
            </div>
            <div class="wheelchair-info">
              <div class="wheelchair-header">
                <h3 class="wheelchair-name">{{ wheelchair.name }}</h3>
                <span class="wheelchair-brand">{{ wheelchair.brand }}</span>
              </div>
              <p class="wheelchair-desc">{{ wheelchair.description }}</p>
              <div class="wheelchair-features">
                <span v-for="feature in wheelchair.features" :key="feature" class="feature-tag">
                  {{ feature }}
                </span>
              </div>
              <div class="wheelchair-price">
                <span class="price" v-if="wheelchair.priceUSD !== undefined">${{ Number(wheelchair.priceUSD).toLocaleString() }}</span>
                <span class="price" v-else>¥{{ wheelchair.price.toLocaleString() }}</span>
                <button class="view-details-btn">View Details</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(-1)">
            Previous
          </button>
          <div class="page-numbers">
            <button 
              v-for="page in visiblePages" 
              :key="page"
              :class="['page-number', { active: page === currentPage }]"
              @click="currentPage = page"
            >
              {{ page }}
            </button>
          </div>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(1)">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Wheelchairs',
  data() {
    return {
      selectedCategory: '',
      selectedUser: '',
      selectedBrand: '',
      selectedRating: 0,
      selectedFeatures: [],
      priceRange: [0, 50000],
      sortBy: 'default',
      itemsPerPage: 6,
      currentPage: 1,
      categories: [
        { id: 'power', name: 'Power Wheelchairs', icon: '⚡' },
        { id: 'manual', name: 'Manual Wheelchairs', icon: '🦽' },
        { id: 'sports', name: 'Sports Wheelchairs', icon: '🏃' },
        { id: 'care', name: 'Care Wheelchairs', icon: '🏥' },
        { id: 'children', name: "Children's Wheelchairs", icon: '👶' }
      ],
      wheelchairs: [
        { id: 1, name: 'W02', description: '1.Untra light , cost performance of carbon fiber,2.Fully automatic electromagnetic brake', image: 'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/w1.png', price: 45000, category: 'power', userType: 'adult', brand: 'SIWEECI', rating: 4.5, features: ['Ultra light', 'Carbon fiber', 'Electromagnetic brake'], productFeatures: ['lightweight', 'foldable'], dateAdded: '2024-04-01', popularity: 80 },
        { id: 2, name: 'W02W widened', description: 'FEATURE:1 .Untra light , cost performance of carbon fiber2.Fully automatic electromagnetic brake3.The handrail can be turned down', image: 'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/w2.png', price: 48000, category: 'power', userType: 'adult', brand: 'SIWEECI', rating: 4.6, features: ['Ultra light', 'Carbon fiber', 'Electromagnetic brake', 'Turnable handrail'], productFeatures: ['adjustable', 'foldable'], dateAdded: '2024-04-02', popularity: 85 },
        { id: 3, name: '208 FDA510K', description: 'FEATURE:1.Untra light , Folding easily for taking by one person2.Electromagnetic brake3.Controller with Led light', image: 'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/w3.png', price: 42000, category: 'power', userType: 'adult', brand: 'SIWEECI', rating: 4.4, features: ['Ultra light', 'Easy folding', 'Electromagnetic brake', 'LED controller'], productFeatures: ['lightweight', 'foldable'], dateAdded: '2024-04-03', popularity: 82 },
        { id: 4, name: '205 FDA510K', description: 'FEATURE:1. Untra light, Folding easily for taking by one person2.Electromagnetic brake3. Folding width is only 26cm', image: 'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/w4.png', price: 40000, category: 'power', userType: 'adult', brand: 'SIWEECI', rating: 4.3, features: ['Ultra light', 'Easy folding', 'Electromagnetic brake', '26cm width'], productFeatures: ['lightweight', 'foldable'], dateAdded: '2024-04-04', popularity: 78 },
        { id: 5, name: '205M FDA510K', description: 'FEATURE:1. Untra light, Folding easily for taking by one person2. Electromagnetic brake3. Folding width is only 26cm', image: 'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/w5.png', price: 41000, category: 'power', userType: 'adult', brand: 'SIWEECI', rating: 4.2, features: ['Ultra light', 'Easy folding', 'Electromagnetic brake', '26cm width'], productFeatures: ['lightweight', 'foldable'], dateAdded: '2024-04-05', popularity: 75 }
      ]
    }
  },
  computed: {
    filteredProducts() {
      let filtered = this.wheelchairs.filter(wheelchair => {
        // 基础筛选
        if (this.selectedCategory && wheelchair.category !== this.selectedCategory) return false;
        if (this.selectedUser && wheelchair.userType !== this.selectedUser) return false;
        if (this.selectedBrand && wheelchair.brand !== this.selectedBrand) return false;
        
        // 价格范围筛选
        if (wheelchair.price < this.priceRange[0] || wheelchair.price > this.priceRange[1]) return false;
        
        // 评分筛选
        if (this.selectedRating > 0 && wheelchair.rating < this.selectedRating) return false;
        
        // 功能特性筛选
        if (this.selectedFeatures.length > 0) {
          const hasSelectedFeature = this.selectedFeatures.some(feature => 
            wheelchair.productFeatures.includes(feature)
          );
          if (!hasSelectedFeature) return false;
        }
        
        return true;
      });
      
      // 排序
      filtered.sort((a, b) => {
        switch (this.sortBy) {
          case 'price-low':
            return a.price - b.price;
          case 'price-high':
            return b.price - a.price;
          case 'rating':
            return b.rating - a.rating;
          case 'newest':
            return new Date(b.dateAdded) - new Date(a.dateAdded);
          case 'popular':
            return b.popularity - a.popularity;
          default:
            return 0;
        }
      });
      
      return filtered;
    },
    totalFilteredProducts() {
      return this.filteredProducts.length;
    },
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProducts.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    activeFilters() {
      const filters = [];
      if (this.selectedCategory) filters.push('Wheelchair Type');
      if (this.selectedUser) filters.push('User Group');
      if (this.selectedBrand) filters.push('Brand');
      if (this.selectedRating > 0) filters.push(`Rating ≥ ${this.selectedRating}`);
      if (this.selectedFeatures.length > 0) filters.push('Features');
      if (this.priceRange[0] > 0 || this.priceRange[1] < 50000) filters.push('Price Range');
      return filters;
    }
  },
  methods: {
    selectCategory(categoryId) {
      this.selectedCategory = categoryId;
      this.currentPage = 1;
    },
    changePage(delta) {
      const newPage = this.currentPage + delta;
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.currentPage = newPage;
      }
    },
    clearFilters() {
      this.selectedCategory = '';
      this.selectedUser = '';
      this.selectedBrand = '';
      this.selectedRating = 0;
      this.selectedFeatures = [];
      this.priceRange = [0, 50000];
      this.sortBy = 'default';
      this.currentPage = 1;
    },
    viewProduct(wheelchair) {
      this.$router.push(`/product/${wheelchair.id}`);
    }
  },
  watch: {
    itemsPerPage() {
      this.currentPage = 1;
    }
  }
}
</script>

<style scoped>
.wheelchairs-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-header {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a7bc8 100%);
  color: white;
  padding: 4rem 2rem;
  text-align: center;
}

.page-title {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 300;
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
}

.category-nav {
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 0;
  position: sticky;
  top: 80px;
  z-index: 100;
}

.category-tabs {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.5rem 0;
}

.category-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  border: none;
  background: transparent;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-size: 1rem;
  color: #666;
}

.category-tab:hover {
  background: #f8f9fa;
  color: #2c5aa0;
}

.category-tab.active {
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  color: white;
  box-shadow: 0 4px 15px rgba(44, 90, 160, 0.3);
}

.category-icon {
  font-size: 1.2rem;
}

.wheelchairs-section {
  padding: 3rem 2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.advanced-filters {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.filter-header h3 {
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
}

.clear-filters {
  background: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.clear-filters:hover {
  background: #e9ecef;
  color: #333;
}

.filter-row {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
}

.filter-group label {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.filter-select {
  padding: 0.8rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.filter-select:focus {
  outline: none;
  border-color: #2c5aa0;
  box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1);
}

/* 价格滑块样式 */
.price-filter {
  flex: 2;
}

.price-slider {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
}

.range-slider {
  position: absolute;
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  background: #2c5aa0;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(44, 90, 160, 0.3);
}

.range-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #2c5aa0;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 6px rgba(44, 90, 160, 0.3);
}

/* 评分筛选样式 */
.rating-filter {
  display: flex;
  gap: 0.2rem;
}

.star {
  font-size: 1.5rem;
  color: #ddd;
  cursor: pointer;
  transition: color 0.3s ease;
}

.star.active,
.star.filled {
  color: #ffd700;
}

.star:hover {
  color: #ffd700;
}

/* 功能特性复选框 */
.feature-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #666;
}

.checkbox-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #2c5aa0;
}

/* 筛选统计 */
.filter-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 0.9rem;
}

.active-filters {
  color: #2c5aa0;
  font-weight: 500;
}

/* 轮椅卡片 */
.wheelchairs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.wheelchair-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  cursor: pointer;
}

.wheelchair-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.wheelchair-image {
  position: relative;
  height: 200px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wheelchair-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.wheelchair-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #ff6b6b;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.wheelchair-rating {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.rating-text {
  font-size: 0.8rem;
  color: #666;
}

.wheelchair-info {
  padding: 1.5rem;
}

.wheelchair-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.wheelchair-name {
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 600;
}

.wheelchair-brand {
  background: #e3f2fd;
  color: #2c5aa0;
  padding: 0.2rem 0.6rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
}

.wheelchair-desc {
  color: #666;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.wheelchair-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.feature-tag {
  background: #e3f2fd;
  color: #2c5aa0;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.wheelchair-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c5aa0;
}

.view-details-btn {
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.view-details-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(44, 90, 160, 0.3);
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
}

.page-btn {
  background: white;
  border: 1px solid #ddd;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #2c5aa0;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 0.5rem;
}

.page-number {
  background: white;
  border: 1px solid #ddd;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 40px;
  text-align: center;
}

.page-number:hover:not(.active) {
  background: #f8f9fa;
  border-color: #2c5aa0;
}

.page-number.active {
  background: #2c5aa0;
  color: white;
  border-color: #2c5aa0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .category-tabs {
    gap: 0.5rem;
  }
  
  .category-tab {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
  
  .filter-row {
    flex-direction: column;
    gap: 1rem;
  }
  
  .filter-group {
    min-width: auto;
  }
  
  .price-filter {
    flex: none;
  }
  
  .feature-checkboxes {
    gap: 0.5rem;
  }
  
  .filter-stats {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
  
  .wheelchairs-grid {
    grid-template-columns: 1fr;
  }
  
  .page-numbers {
    display: none;
  }
}
</style> 